/**
 * SQL formatter and minifier.
 *
 * Beautifying changes whitespace and the case of recognised keywords, and nothing else:
 * every token comes out in the order it went in (the tests check this on every sample).
 * Minifying removes whitespace and comments, but keeps the comments a database acts on
 * (optimizer hints and MySQL's versioned comments).
 */

import {
  type LayoutContext,
  layoutBlock,
} from "@/lib/tool-kit/latex-dev/sql-layout";
import {
  type SqlDialect,
  type SqlToken,
  tokenizeSql,
} from "@/lib/tool-kit/latex-dev/sql-tokenizer";
import {
  buildTree,
  classifyStatement,
  type KeywordCase,
  mergeCompounds,
  type Node,
} from "@/lib/tool-kit/latex-dev/sql-tree";

export type { SqlDialect } from "@/lib/tool-kit/latex-dev/sql-tokenizer";
export type { KeywordCase } from "@/lib/tool-kit/latex-dev/sql-tree";

export type SqlIndent = "2" | "4" | "tab";
export type SqlMode = "beautify" | "minify";

export interface SqlFormatOptions {
  dialect: SqlDialect;
  keywordCase: KeywordCase;
  indent: SqlIndent;
  /** Put the comma at the start of the next line. */
  commaFirst: boolean;
  /** Break brackets that would run past this width. */
  maxWidth: number;
  /** Leave an empty line between statements. */
  blankLines: boolean;
}

export const DEFAULT_SQL_OPTIONS: SqlFormatOptions = {
  dialect: "standard",
  keywordCase: "upper",
  indent: "2",
  commaFirst: false,
  maxWidth: 80,
  blankLines: true,
};

export interface SqlMinifyOptions {
  dialect: SqlDialect;
  /** Keep ordinary comments (line comments become block comments). */
  keepComments: boolean;
}

export const DEFAULT_MINIFY_OPTIONS: SqlMinifyOptions = {
  dialect: "standard",
  keepComments: false,
};

export interface SqlResult {
  text: string;
  statements: number;
  tokens: number;
  /** Problems found while reading: unbalanced brackets, unterminated strings. */
  warnings: SqlWarning[];
}

export type SqlWarning = "brackets" | "string" | "comment";

const INDENT_UNITS: Readonly<Record<SqlIndent, string>> = {
  "2": "  ",
  "4": "    ",
  tab: "\t",
};

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

function isUnterminated(tok: SqlToken): boolean {
  const text = tok.text;
  if (tok.type === "blockComment") {
    return !text.endsWith("*/");
  }
  if (tok.type === "string" || tok.type === "quoted") {
    const first = text[0];
    if (first === "[") {
      return !text.endsWith("]");
    }
    const quote = text.includes("'") && tok.type === "string" ? "'" : first;
    return text.length < 2 || !text.endsWith(quote);
  }
  return false;
}

function warningsFor(tokens: SqlToken[], tree: Node[]): SqlWarning[] {
  const found = new Set<SqlWarning>();
  for (const tok of tokens) {
    if (tok.type === "blockComment" && isUnterminated(tok)) {
      found.add("comment");
    } else if (isUnterminated(tok) && tok.text.length > 0) {
      found.add("string");
    }
  }
  if (unbalanced(tree)) {
    found.add("brackets");
  }
  return [...found];
}

function unbalanced(nodes: Node[]): boolean {
  return nodes.some((n) => {
    if (n.kind === "tok") {
      return n.tok.type === "close" || n.tok.type === "bracketClose";
    }
    const unclosed = n.close === null;
    return unclosed || unbalanced(n.items);
  });
}

interface Statement {
  nodes: Node[];
  /** The terminator: ";" or a SQL Server "GO" batch separator. */
  terminator: string;
}

function isBatchSeparator(
  node: Node,
  next: Node | undefined,
  dialect: SqlDialect
): boolean {
  return (
    dialect === "sqlserver" &&
    node.kind === "tok" &&
    node.upper === "GO" &&
    node.tok.nl > 0 &&
    (next === undefined ||
      (next.kind === "tok" && next.tok.nl > 0) ||
      next.kind === "group")
  );
}

function splitStatements(root: Node[], dialect: SqlDialect): Statement[] {
  const out: Statement[] = [];
  let current: Node[] = [];
  root.forEach((node, index) => {
    const isSemicolon = node.kind === "tok" && node.tok.type === "semicolon";
    const isGo = isBatchSeparator(node, root[index + 1], dialect);
    if (isSemicolon || isGo) {
      out.push({ nodes: current, terminator: isGo ? "GO" : ";" });
      current = [];
    } else {
      current.push(node);
    }
  });
  if (current.length > 0) {
    out.push({ nodes: current, terminator: "" });
  }
  return out;
}

function endsInLineComment(nodes: Node[]): boolean {
  const last = nodes.at(-1);
  return last?.kind === "tok" && last.tok.type === "lineComment";
}

// ---------------------------------------------------------------------------
// Beautify
// ---------------------------------------------------------------------------

function statementLines(
  statement: Statement,
  options: SqlFormatOptions,
  ctx: LayoutContext
): string[] {
  classifyStatement(statement.nodes, {
    dialect: options.dialect,
    keywordCase: options.keywordCase,
  });
  const lines = layoutBlock(statement.nodes, 0, ctx);
  const end = statement.terminator;
  if (end === "") {
    return lines;
  }
  if (end === "GO") {
    return [...lines, "GO"];
  }
  if (lines.length === 0 || endsInLineComment(statement.nodes)) {
    return [...lines, ";"];
  }
  lines[lines.length - 1] += ";";
  return lines;
}

/** Lay out SQL: one clause per line, nested queries indented, keywords in one case. */
export function formatSql(
  source: string,
  options: SqlFormatOptions = DEFAULT_SQL_OPTIONS
): SqlResult {
  const tokens = mergeCompounds(tokenizeSql(source, options.dialect));
  const tree = buildTree(tokens);
  const ctx: LayoutContext = {
    unit: INDENT_UNITS[options.indent],
    maxWidth: options.maxWidth,
    commaFirst: options.commaFirst,
  };
  const statements = splitStatements(tree, options.dialect);
  const blocks = statements.map((s) =>
    statementLines(s, options, ctx).join("\n")
  );
  return {
    text: blocks.join(options.blankLines ? "\n\n" : "\n"),
    statements: statements.filter((s) => s.nodes.length > 0).length,
    tokens: tokens.length,
    warnings: warningsFor(tokens, tree),
  };
}

// ---------------------------------------------------------------------------
// Minify
// ---------------------------------------------------------------------------

const ATOMIC: ReadonlySet<SqlToken["type"]> = new Set([
  "word",
  "number",
  "quoted",
  "string",
  "param",
]);

const TIGHT_BEFORE: ReadonlySet<SqlToken["type"]> = new Set([
  "close",
  "comma",
  "semicolon",
  "dot",
  "bracketClose",
]);

const TIGHT_AFTER: ReadonlySet<SqlToken["type"]> = new Set([
  "open",
  "dot",
  "bracketOpen",
  "comma",
  "semicolon",
]);

/** A comment the database acts on: an optimizer hint or a MySQL versioned comment. */
function isSignificantComment(tok: SqlToken): boolean {
  return (
    tok.type === "blockComment" &&
    (tok.text.startsWith("/*+") || tok.text.startsWith("/*!"))
  );
}

function isCommentType(type: SqlToken["type"]): boolean {
  return type === "lineComment" || type === "blockComment";
}

const RE_COMMENT_LEAD = /^(--|#)\s?/;

function asBlockComment(tok: SqlToken): string {
  if (tok.type === "blockComment") {
    return tok.text;
  }
  const body = tok.text.replace(RE_COMMENT_LEAD, "").replaceAll("*/", "* /");
  return `/* ${body.trim()} */`;
}

function minifySpace(prev: SqlToken | null, next: SqlToken): boolean {
  if (!prev) {
    return false;
  }
  if (TIGHT_BEFORE.has(next.type) || TIGHT_AFTER.has(prev.type)) {
    return false;
  }
  // IN(…), COUNT(…), VALUES(…): no space is needed before a bracket.
  if (next.type === "open" || next.type === "bracketOpen") {
    return false;
  }
  if (isCommentType(prev.type) || isCommentType(next.type)) {
    return true;
  }
  if (ATOMIC.has(prev.type) && ATOMIC.has(next.type)) {
    return true;
  }
  if (prev.type === "operator" && next.type === "operator") {
    return true;
  }
  const closes = prev.type === "close" || prev.type === "bracketClose";
  return closes && ATOMIC.has(next.type);
}

/** Remove whitespace and comments, keeping the comments that change behaviour. */
export function minifySql(
  source: string,
  options: SqlMinifyOptions = DEFAULT_MINIFY_OPTIONS
): SqlResult {
  const all = mergeCompounds(tokenizeSql(source, options.dialect));
  const tree = buildTree(all);
  const tokens = all.filter((tok) => {
    const comment = tok.type === "lineComment" || tok.type === "blockComment";
    return !comment || options.keepComments || isSignificantComment(tok);
  });
  let out = "";
  let prev: SqlToken | null = null;
  for (const tok of tokens) {
    const text = tok.type === "lineComment" ? asBlockComment(tok) : tok.text;
    out += (minifySpace(prev, tok) ? " " : "") + text;
    prev = tok;
  }
  return {
    text: out,
    statements: splitStatements(tree, options.dialect).filter(
      (s) => s.nodes.length > 0
    ).length,
    tokens: all.length,
    warnings: warningsFor(all, tree),
  };
}
